import { HOLDABLE_STATUSES, ORDER_STATUS, PAYMENT_METHOD } from './orderConstants';
import { PAYMENT_STATUS } from '../../../../lib/paymentStatus';
import { isExitStatus, isHoldStatus, isReturnStatus } from './orderStatus';

/*
 * Whether an order's contents can be edited, and if not, what to do about it.
 *
 * The house rule: **an order must be on hold before it can be changed.**
 *
 * That is a policy decision, not a backend one — the API will accept edits from
 * an ordinary active order. Requiring the pause first buys three things:
 *
 *   · the change is deliberate rather than incidental;
 *   · the hold status makes it visible to everyone else that the order is being
 *     worked on, so nobody picks or dispatches it mid-edit;
 *   · hold is super-admin-only, so contents changes inherit that restriction
 *     without needing a second permission.
 *
 * Because it is a UI policy, it is a strong convention rather than a guarantee:
 * anything calling the API directly bypasses it. If it needs to be absolute,
 * the backend has to require hold too.
 *
 * The awkward edge: the backend only allows hold from the pre-dispatch stages
 * (New Order, Confirmed, Processing), so an order already handed to the
 * shipping company can't be held directly. Moving it back to Confirmed is an
 * ordinary transition, and hold is legal from there — so "Hold & edit"
 * performs both moves rather than handing the operator a two-step
 * instruction. `canEnterHoldFrom` is what tells the caller whether the extra
 * step is needed, and the confirmation names it before it happens.
 */

export const EDIT_BLOCKED = {
  NO_PERMISSION: 'no-permission',
  TAMARA: 'tamara',
  PAYMENT: 'payment',
  ENDED: 'ended',
  COMPLETED: 'completed',
  NOT_HELD: 'not-held',
  NEEDS_RESUME_PATH: 'needs-resume-path',
  NOT_SUPER_ADMIN: 'not-super-admin',
};

/** Hold can only be entered from these — the backend refuses anywhere else. */
export const canEnterHoldFrom = (statusId) =>
  HOLDABLE_STATUSES.includes(Number(statusId));

const REASONS = {
  [EDIT_BLOCKED.NO_PERMISSION]: {
    reason: 'You don’t have permission to edit orders.',
  },
  [EDIT_BLOCKED.TAMARA]: {
    reason: 'Tamara orders can’t be edited after checkout.',
  },
  [EDIT_BLOCKED.PAYMENT]: {
    reason: 'Only paid orders can be edited.',
  },
  [EDIT_BLOCKED.ENDED]: {
    reason: 'This order has ended, so its contents are fixed.',
  },
  [EDIT_BLOCKED.COMPLETED]: {
    reason:
      'This order is completed. Move it back to an active status, then put it on hold to edit it.',
  },
  [EDIT_BLOCKED.NOT_HELD]: {
    reason:
      'Put this order on hold to add, remove or change quantities. Removing an item is how you cancel it — the totals recalculate.',
    // The UI can offer the hold directly from here.
    canOfferHold: true,
  },
  [EDIT_BLOCKED.NEEDS_RESUME_PATH]: {
    // Hold isn't reachable directly from here, but Confirmed is — and hold is
    // reachable from Confirmed. The button performs both moves.
    reason:
      'To add, remove or change items, this order has to be moved back to Confirmed and put on hold.',
    canOfferHold: true,
  },
  [EDIT_BLOCKED.NOT_SUPER_ADMIN]: {
    reason:
      'Changing an order’s contents means putting it on hold, which only a super admin can do.',
  },
};

/**
 * @returns {{
 *   canEdit: boolean,
 *   blockedBy: string|null,
 *   reason: string|null,
 *   canOfferHold: boolean,
 * }}
 */
export const resolveEditability = ({
  order,
  hasEditPermission,
  isSuperAdmin,
}) => {
  const blocked = (code) => ({
    canEdit: false,
    blockedBy: code,
    reason: REASONS[code]?.reason ?? null,
    canOfferHold: Boolean(REASONS[code]?.canOfferHold) && isSuperAdmin,
  });

  if (!hasEditPermission || !order) return blocked(EDIT_BLOCKED.NO_PERMISSION);

  const status = Number(order.status);
  const paymentStatus = Number(order.payment_status);

  // Hard blocks first — conditions the backend enforces regardless of hold.
  if (Number(order.payment_method) === PAYMENT_METHOD.TAMARA) {
    return blocked(EDIT_BLOCKED.TAMARA);
  }
  if (paymentStatus !== PAYMENT_STATUS.PAID) {
    return blocked(EDIT_BLOCKED.PAYMENT);
  }
  // Every exit is final: cancelled, and the retired rejected. A returned
  // order is not ended — it falls through to the move-back-and-hold path.
  if (isExitStatus(status)) return blocked(EDIT_BLOCKED.ENDED);
  if (status === ORDER_STATUS.DELIVERED) return blocked(EDIT_BLOCKED.COMPLETED);

  // Then the house rule.
  if (isHoldStatus(status)) {
    // Held orders are already super-admin-only server-side; this keeps the UI
    // from showing controls that would come back as 422.
    return isSuperAdmin
      ? { canEdit: true, blockedBy: null, reason: null, canOfferHold: false }
      : blocked(EDIT_BLOCKED.NOT_SUPER_ADMIN);
  }

  if (!isSuperAdmin) return blocked(EDIT_BLOCKED.NOT_SUPER_ADMIN);

  return canEnterHoldFrom(status)
    ? blocked(EDIT_BLOCKED.NOT_HELD)
    : blocked(EDIT_BLOCKED.NEEDS_RESUME_PATH);
};

/*
 * A line can't be removed if it's the only active one left — the backend says
 * "cancel the whole order instead", which is the right instruction, so the
 * button is disabled with that as its tooltip rather than firing and failing.
 */
/*
 * A line in an exit state — cancelled, the retired rejected — is dead: no
 * value, no stock claim, nothing left to change. A returned line is not
 * dead: it comes back to life when the parcel is sent out again.
 */
export const activeItems = (items = []) =>
  items.filter((item) => !isExitStatus(item.status));

export const isLastActiveItem = (items, item) => {
  const active = activeItems(items);
  return active.length <= 1 && active.some((one) => one.id === item.id);
};

/** Dead, returned and delivered lines can't have their quantity changed. */
export const canChangeQuantity = (item) => {
  const status = Number(item?.status);
  return !isExitStatus(status) && !isReturnStatus(status) && status !== ORDER_STATUS.DELIVERED;
};

/*
 * Struck through on screen: the goods are not with the customer. Returned
 * lines read this way too, until the parcel goes out again.
 */
export const isCancelledItem = (item) =>
  isExitStatus(item?.status) || isReturnStatus(item?.status);
